import { ApolloProvider } from '@apollo/react-hooks';
import * as Sentry from '@sentry/browser';
import React, { useCallback, useEffect, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import { locales } from 'app/locales';
import { IntlProvider } from 'react-intl';

import createApolloClient from 'app/utils/apollo/client';

import GlobalStyle from 'app/global-styles';
import { theme } from 'app/providers/theme';

import useConfig, { ConfigProvider } from 'app/config';
import useServices, { ServicesProvider } from 'app/services';

import { TokenPayload } from 'app/services/auth';

import { refreshTokens, signout } from 'actions/auth';
import { AppRoutes } from 'app/routes/app-routes';
import { StateProvider, useAppState, useDispatch } from 'state';

import { AnalyticsProvider } from 'app/utils/analytics/provider';
import { identifyEvent } from 'src/app/utils/analytics/amplitude/client';
import { UserProperties } from 'src/app/utils/analytics/amplitude/types';
import { CommunicationProvider, useCommunicationService } from 'src/app/services/communication';
import { MessageType } from 'src/app/utils/in-app-comunication/types';

// Navigates to a URL when a 'whiteboard-open' message is received from window messages
const WindowMessageNavigator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const comms = useCommunicationService();
  useEffect(() => {
    const unsubscribe = comms.onMessage((message) => {
      if (message.type === MessageType.WHITEBOARD_OPEN && message.url && location.pathname === '/') {
        navigate(message.url);
      }
    });
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [comms, navigate, location.pathname]);
  return null;
};

const PageWrapper = () => {
  const { config } = useConfig();
  const services = useServices();
  const dispatch = useDispatch();

  const { signedIn, expires } = useAppState(({ auth }) => ({
    signedIn: auth.signedIn,
    expires: auth.expires,
  }));

  useEffect(() => {
    if (signedIn && expires && expires <= new Date()) {
      dispatch(signout());
    }
  }, [signedIn, expires, dispatch]);

  const onTokenRefresh = useCallback(
    (payload: TokenPayload) => (payload.refreshToken ? dispatch(refreshTokens(payload)) : dispatch(signout())),
    [dispatch],
  );

  const onTokenRefreshError = useCallback(() => dispatch(signout()), [dispatch]);

  const { client } = useMemo(
    () =>
      createApolloClient({
        uri: config.REACT_APP_HYROX_API_URL,
        authService: services.auth,
        onTokenRefresh,
        onTokenRefreshError,
      }),
    [config.REACT_APP_HYROX_API_URL],
  );

  return (
    <ApolloProvider client={client}>
      <AppRoutes />
    </ApolloProvider>
  );
};

const AppWithLocale = () => {
  const languageCode = useAppState(({ language }) => language?.preferredLanguage?.code?.toLowerCase()) || 'en';

  useEffect(() => {
    identifyEvent.set(UserProperties.PREFERRED_LANGUAGE, languageCode);
  }, [languageCode]);

  return (
    <IntlProvider defaultLocale="en" locale={languageCode} messages={locales[languageCode]}>
      <Router>
        <WindowMessageNavigator />
        <PageWrapper />
      </Router>
    </IntlProvider>
  );
};

const AppWithConfig = () => {
  const { config } = useConfig();

  useEffect(() => {
    if (config.REACT_APP_SENTRY_DSN) {
      Sentry.init({
        dsn: config.REACT_APP_SENTRY_DSN,
        environment: config.REACT_APP_SENTRY_ENVIRONMENT,
      });
    }
  }, [config.REACT_APP_SENTRY_DSN, config.REACT_APP_SENTRY_ENVIRONMENT]);

  return (
    <ThemeProvider theme={theme}>
      <AnalyticsProvider config={config}>
        <ServicesProvider config={config}>
          <CommunicationProvider>
            <GlobalStyle />
            <StateProvider>
              <AppWithLocale />
            </StateProvider>
          </CommunicationProvider>
        </ServicesProvider>
      </AnalyticsProvider>
    </ThemeProvider>
  );
};

const App = () => (
  <ConfigProvider>
    <AppWithConfig />
  </ConfigProvider>
);

const root = createRoot(document.getElementById('root') || document.createElement('div'));
root.render(<App />);
